import React, { useEffect, useState } from "react";
import { Bell, Tag, Globe, Languages, Sparkles, Loader2, RefreshCcw, ChevronDown, Clock, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { useCollapse } from "../../lib/cockpitCollapse";

const ICONS = { bell: Bell, tag: Tag, globe: Globe, languages: Languages };
const HINT_ICONS = { clock: Clock, alert: AlertTriangle, bell: Bell };

function timeAgo(iso) {
    if (!iso) return "";
    try {
        const diff = (Date.now() - new Date(iso).getTime()) / 1000;
        if (diff < 60) return "gerade eben";
        if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`;
        if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
        return `vor ${Math.floor(diff / 86400)} Tagen`;
    } catch {
        return "";
    }
}

/**
 * KI-Aktivitäts-Feed. Jun-2026 (User-Wunsch 1a):
 * Der Feed ist nach Thema getrennt — `area="ankauf"` zeigt NUR Ankauf-Themen
 * (gespeicherte Suchen/Treffer), `area="verkauf"` NUR Verkauf-Themen
 * (KI-Tags, SEO, Übersetzungen der eigenen Inserate). Ohne `area` → alles.
 * Die gesamte Karte ist einklappbar (Standard: eingeklappt).
 */
export default function AiActivityFeedCard({ area, onJump }) {
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [data, setData] = useState({ activities: [], stats: {} });
    const [error, setError] = useState(null);
    const [collapsed, toggle] = useCollapse(`ai_activity_${area || "all"}`, true);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get("/dashboard/ai-activity");
            setData(res.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Konnte KI-Aktivitäts-Feed nicht laden.");
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    };

    // Beim Aufklappen erstmals laden (lazy) — funktioniert für Klick,
    // bereits-ausgeklappt-beim-Mount UND „Alle aufklappen". Verzögert, damit
    // kein synchrones setState im Effekt steht.
    const handleToggle = () => toggle();

    useEffect(() => {
        if (!collapsed && !loaded) {
            const t = setTimeout(load, 0);
            return () => clearTimeout(t);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collapsed, loaded]);

    const allActivities = data.activities || [];
    const stats = data.stats || {};
    const hints = area === "werkstatt" ? (data.hints || []) : [];
    const activities = area
        ? allActivities.filter((a) => a.topic === area)
        : allActivities;

    const title = area === "ankauf"
        ? "KI-Aktivitäts-Feed · Ankauf"
        : area === "verkauf"
            ? "KI-Aktivitäts-Feed · Verkauf"
            : area === "werkstatt"
                ? "KI-Aktivitäts-Feed · Werkstatt"
                : "KI-Aktivitäts-Feed";

    // Themen-spezifische Kennzahlen. Im Ankauf/Werkstatt sind die Kacheln klickbar
    // und springen direkt zum jeweiligen Widget (jump = {area, target-testid}).
    const tiles = area === "verkauf"
        ? [
            { label: "KI-Inserate", value: stats.ai_enhanced_listings ?? 0, testid: "ai-stat-enhanced" },
            { label: "KI-Aktionen", value: activities.length, testid: "ai-stat-verkauf-actions" },
        ]
        : area === "ankauf"
            ? [
                { label: "Ankäufe (B2C / Privat)", value: stats.buy_requests ?? 0, testid: "ai-stat-buyrequests", jump: { area: "ankauf", target: "ankauf-list-ankauf_buy_requests" } },
                { label: "Inzahlungnahmen / Tausch", value: stats.trade_ins ?? 0, testid: "ai-stat-tradeins", jump: { area: "ankauf", target: "ankauf-list-ankauf_tradeins" } },
                { label: "Favoriten / Merkliste", value: stats.favorites ?? 0, testid: "ai-stat-favorites", jump: { area: "ankauf", target: "favorites-widget" } },
                { label: "Gespeicherte Suchen", value: stats.saved_searches ?? 0, testid: "ai-stat-saved", jump: { area: "ankauf", target: "saved-searches-widget" } },
                { label: "Getrackte Views", value: stats.tracked_views ?? 0, testid: "ai-stat-views", jump: { area: "verkauf", target: "cockpit-widget-statistics" } },
            ]
            : area === "werkstatt"
                ? [
                    { label: "Termine heute", value: stats.workshop_today ?? 0, testid: "ai-stat-workshop-today", jump: { area: "werkstatt", target: "cockpit-collapse-workshop_today" } },
                    { label: "Offene Anfragen", value: stats.workshop_open ?? 0, testid: "ai-stat-workshop-open", jump: { area: "werkstatt", target: "cockpit-collapse-workshop_today" } },
                ]
                : [
                    { label: "KI-Inserate", value: stats.ai_enhanced_listings ?? 0, testid: "ai-stat-enhanced" },
                    { label: "Gespeicherte Suchen", value: stats.saved_searches ?? 0, testid: "ai-stat-saved" },
                    { label: "Favoriten", value: stats.favorites ?? 0, testid: "ai-stat-favorites" },
                    { label: "Getrackte Views", value: stats.tracked_views ?? 0, testid: "ai-stat-views" },
                ];

    const gridCols = tiles.length <= 2
        ? "grid-cols-2"
        : tiles.length === 5
            ? "grid-cols-2 md:grid-cols-5"
            : "grid-cols-2 md:grid-cols-4";

    return (
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-testid={`ai-activity-feed-card${area ? `-${area}` : ""}`}>
            <button
                type="button"
                onClick={handleToggle}
                className={`w-full px-5 py-3 flex items-center gap-2 hover:bg-gray-50 transition-colors text-left ${!collapsed ? "border-b border-gray-100" : ""}`}
                data-testid="ai-activity-toggle"
                aria-expanded={!collapsed}
            >
                <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
                <Sparkles className="w-4 h-4 text-twowheels-yellow-foreground shrink-0" />
                <h3 className="text-base font-semibold text-gray-900 truncate">{title}</h3>
            </button>

            {!collapsed && (
                <div className="p-5">
                    <div className="flex items-center justify-end mb-3">
                        <button
                            type="button"
                            onClick={load}
                            className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1"
                            data-testid="ai-activity-refresh"
                        >
                            <RefreshCcw className="w-3 h-3" /> Neu laden
                        </button>
                    </div>

                    {area === "werkstatt" && hints.length > 0 && (
                        <div className="mb-4 space-y-2" data-testid="ai-werkstatt-hints">
                            {hints.map((h, i) => {
                                const HIcon = HINT_ICONS[h.icon] || Sparkles;
                                const warn = h.severity === "warning";
                                return (
                                    <div
                                        key={i}
                                        className={`flex items-start gap-2.5 rounded-md border px-3 py-2 ${warn ? "border-amber-300 bg-amber-50" : "border-blue-200 bg-blue-50"}`}
                                        data-testid={`ai-werkstatt-hint-${i}`}
                                    >
                                        <HIcon className={`w-4 h-4 mt-0.5 shrink-0 ${warn ? "text-amber-600" : "text-blue-600"}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className={`text-sm font-semibold ${warn ? "text-amber-900" : "text-blue-900"}`}>{h.title}</div>
                                            {h.detail && <div className="text-xs text-gray-600 truncate">{h.detail}</div>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className={`grid gap-2 mb-4 text-center ${gridCols}`}>
                        {tiles.map((t) => {
                            const clickable = onJump && t.jump;
                            const inner = (
                                <>
                                    <div className="text-[10px] uppercase tracking-wider text-gray-500">{t.label}</div>
                                    <div className="text-lg font-bold text-gray-900" data-testid={t.testid}>{t.value}</div>
                                </>
                            );
                            return clickable ? (
                                <button
                                    key={t.testid}
                                    type="button"
                                    onClick={() => onJump(t.jump.area, t.jump.target)}
                                    className="bg-gray-50 border border-gray-200 rounded-sm p-2 hover:border-[#FFCC00] hover:bg-[#FFCC00]/10 transition-colors cursor-pointer"
                                    data-testid={`${t.testid}-jump`}
                                    title="Zum Bereich springen"
                                >
                                    {inner}
                                </button>
                            ) : (
                                <div key={t.testid} className="bg-gray-50 border border-gray-200 rounded-sm p-2">
                                    {inner}
                                </div>
                            );
                        })}
                    </div>

                    {loading && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" /> Lade KI-Aktivitäten…
                        </div>
                    )}
                    {error && !loading && (
                        <div className="text-sm text-red-600 py-4" data-testid="ai-activity-error">{error}</div>
                    )}
                    {!loading && !error && activities.length === 0 && (
                        <div className="text-sm text-gray-500 py-6 text-center" data-testid="ai-activity-empty">
                            {area === "verkauf"
                                ? "Noch keine KI-Aktivitäten zu Ihren Inseraten. Beim Anlegen ergänzt die KI SEO-Daten, Tags und Übersetzungen."
                                : area === "ankauf"
                                    ? "Noch keine Ankauf-KI-Aktivitäten. Speichern Sie eine Suche, und wir benachrichtigen Sie bei neuen Treffern."
                                    : "Noch keine KI-Aktivitäten."}
                        </div>
                    )}
                    {!loading && !error && activities.length > 0 && (
                        <ul className="divide-y divide-gray-100" data-testid="ai-activity-list">
                            {activities.slice(0, 12).map((a, i) => {
                                const Icon = ICONS[a.icon] || Sparkles;
                                return (
                                    <li key={i} className="py-2.5 flex items-start gap-3" data-testid={`ai-activity-item-${i}`}>
                                        <span className="mt-0.5 w-7 h-7 rounded-full bg-twowheels-yellow/20 flex items-center justify-center shrink-0">
                                            <Icon className="w-3.5 h-3.5 text-twowheels-yellow-foreground" />
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-gray-900 truncate">{a.title}</div>
                                            {a.detail && <div className="text-xs text-gray-600 truncate">{a.detail}</div>}
                                            <div className="text-[11px] text-gray-500 mt-0.5">{timeAgo(a.at)}</div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
